import axios from "axios"
import { showErrorToast } from "@/lib/toast"
import { auth } from "@/lib/firebase"

const url = import.meta.env.VITE_BACKEND_SERVER

const api = axios.create({
  baseURL: url,
})

// Create a Firebase auth promise to check the current user's authentication state
const firebaseAuthPromise = new Promise((resolve) => {
  const unsubscribe = auth.onAuthStateChanged((user) => {
    unsubscribe()
    resolve(user)
  })
})

// Timeout for Firebase initialization
const timeoutPromise = new Promise((resolve, reject) => {
  setTimeout(() => {
    reject(new Error("Firebase app initialization timed out"))
  }, 5000) // Adjust the timeout duration as needed
})

// Axios request interceptor to attach the Authorization header with the Firebase token
api.interceptors.request.use(async (config) => {
  try {
    await Promise.race([firebaseAuthPromise, timeoutPromise])

    const user = auth.currentUser

    if (user) {
      // Fetch a new token or refresh the token if expired
      const idToken = await user.getIdToken()

      if (idToken && idToken.length > 0) {
        // Create a new headers object with the existing headers and the new Authorization header
        const newHeaders = {
          ...config.headers,
          Authorization: `Bearer ${idToken}`,
        }

        // Return a new config object with the updated headers
        return {
          ...config,
          headers: newHeaders,
        }
      }

      // If no token, return the original config
      return config
    }

    return config
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error in request interceptor", error)
    return Promise.reject(error) // You can handle redirection or show an error message here
  }
})

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 unauthorized
    if (error.response?.status === 401) {
      window.location.href = "/login"
      return Promise.reject(error)
    }

    // Show error toast for other errors
    if (error.response?.status !== 401) {
      showErrorToast(error)
    }

    return Promise.reject(error)
  }
)

export default api
